import { Info, Send } from '@mui/icons-material';
import { Box, Divider, IconButton, Stack, Typography } from '@mui/material';
import { marked } from 'marked';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useUser } from '@work4all/data';

import { CheckboxRadioItem } from '../../../input/checkbox-radio-item';
import { LabeledInput } from '../../../input/labeled-input';
import { Caption } from '../../../typography/caption/Caption';
import { ToniAIMapping } from '../../types';
import { ToniAILoadingIndicator } from '../toni-ai-chat-bot-loading-indicator/ToniAIChatBotLoadingIndicator';
import { useToniAIContext } from '../ToniAIChatBotProvider';

const HELPCENTER_URL = process.env.NX_PUBLIC_TONI_AI_HELPCENTER_URL;
export interface ToniAIMiniProps {
  aiToolsUsageEnabled: boolean;
  setAiToolsUsageEnabled: (enabled: boolean) => void;
  onSuccess: (
    message: string,
    mapping: ToniAIMapping,
    completed: boolean
  ) => void;
}

export const ToniAIMini = (props: ToniAIMiniProps) => {
  const { onSuccess, aiToolsUsageEnabled, setAiToolsUsageEnabled } = props;
  const { term, setTerm, triggerGetEmail, loading, messages, currentStream } =
    useToniAIContext();

  const user = useUser();

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const [submitted, setSubmitted] = useState(false);
  const [mapping, setMapping] = useState(null);

  const handleSubmit = () => {
    triggerGetEmail();
    setSubmitted(true);
  };

  const returnResult = useCallback(
    async (message: string, mapping: ToniAIMapping, completed: boolean) => {
      const html = await marked.parse(message || '', {
        breaks: true,
      });
      onSuccess(html, mapping, completed);
    },
    [onSuccess]
  );

  useEffect(() => {
    if (!submitted) return;
    if (currentStream) {
      returnResult(currentStream, mapping, false);
    }
  }, [currentStream, returnResult, mapping, submitted]);

  useEffect(() => {
    if (!submitted) return;
    const message = messages[messages.length - 1];

    const incomingMapping = message?.incoming?.assembled_result?.mapping;

    if (incomingMapping) {
      setMapping(incomingMapping);
    }
    if (message.incoming?.is_complete) {
      returnResult(
        message.incoming.assembled_result?.response,
        incomingMapping,
        true
      );
      setSubmitted(false);
    }
  }, [loading, messages, returnResult, submitted]);

  const { t } = useTranslation();

  return (
    <Stack p="0.5rem" gap="0.5rem">
      {!aiToolsUsageEnabled && (
        <>
          <Typography variant="h4">{t('COMMON.TONI_AI.TITLE')}</Typography>
          <Typography>{t('COMMON.TONI_AI.AI_TOOLS_USAGE_DISABLED')}</Typography>
          <Typography>{t('COMMON.TONI_AI.AI_TOOLS_DESCRIPTION')}</Typography>
          {user.isMaster && (
            <Typography>
              <Link target="_blank" to={HELPCENTER_URL}>
                {t('COMMON.TONI_AI.READ_MORE')}
              </Link>
            </Typography>
          )}
          {!user.isMaster && (
            <Typography>
              {t('COMMON.TONI_AI.AI_TOOLS_USAGE_DISABLED_ASK_ADMIN')}
            </Typography>
          )}
        </>
      )}
      {!loading && aiToolsUsageEnabled && (
        <>
          <form name="toni-ai-form" onSubmit={handleSubmit}>
            <LabeledInput
              disabled={loading}
              defaultValue={term}
              onChange={(e) => setTerm(e.target.value)}
              style={{
                width: '40rem',
                maxWidth: '100%',
              }}
              inputProps={{
                alignContent: 'center',
              }}
              placeholder={t('TONI_AI.PLACEHOLDER')}
              ref={inputRef}
              multiline
              multilineWithDynamicHeight
              endAdornment={
                <Box display="flex" alignSelf="end">
                  <IconButton
                    onClick={handleSubmit}
                    disabled={loading || !term}
                    size="small"
                  >
                    <Send />
                  </IconButton>
                </Box>
              }
              minRows={1}
              onKeyDown={(e) => {
                if (e.ctrlKey && e.code === 'Enter' && term) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            />
          </form>
          <Stack
            direction="row"
            width="100%"
            gap="0.25rem"
            justifyContent="space-between"
          >
            <Box
              display="flex"
              alignItems="center"
              gap="0.25rem"
              onClick={handleSubmit}
              style={{
                cursor: 'pointer',
                color: 'var(--text03)',
              }}
            >
              <Send fontSize="inherit" color="inherit" />
              <Caption color="text03">Strg</Caption>
              <Caption color="text03">+</Caption>
              <Caption color="text03">Enter</Caption>
            </Box>
            <Link
              target="_blank"
              to={HELPCENTER_URL}
              style={{ textDecoration: 'none' }}
            >
              <Box
                display="flex"
                alignItems="center"
                gap="0.25rem"
                style={{
                  cursor: 'pointer',
                  color: 'var(--text03)',
                }}
              >
                <Info fontSize="inherit" color="inherit" />
                <Typography color="text03">
                  {t('COMMON.TONI_AI.READ_MORE')}
                </Typography>
              </Box>
            </Link>
          </Stack>
        </>
      )}
      {loading && <ToniAILoadingIndicator />}
      {user.isMaster && (
        <>
          <Divider />
          <CheckboxRadioItem
            control="checkbox"
            label={t('COMMON.TONI_AI.AI_TOOLS_USAGE_ENABLED')}
            checked={aiToolsUsageEnabled}
            onChange={() => setAiToolsUsageEnabled(!aiToolsUsageEnabled)}
          />
        </>
      )}
    </Stack>
  );
};
